/**
 * TagAdminPage
 *
 * Admin page for managing tags: edit name, color, icon, auto_download, isNsfw.
 * Accessible via CMD+K "Tag Admin" or ALT+click on any tag.
 */

import { Download, Loader2, Pencil, Save, Search, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authFetch, type TagResponse } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';

interface FullTagResponse extends TagResponse {
	isNsfw?: boolean;
	autoDownload?: boolean;
	createdAt?: string;
}

// Fetch all tags from REST API
async function fetchAllTags(): Promise<FullTagResponse[]> {
	const res = await authFetch('/api/tags');
	if (!res.ok) throw new Error(`Failed to fetch tags: ${res.status}`);
	const data = await res.json();
	return data.tags;
}

// Update a tag via REST API
async function updateTag(
	id: number,
	updates: { name?: string; color?: string; icon?: string; autoDownload?: boolean }
): Promise<FullTagResponse> {
	const res = await authFetch(`/api/tags/${id}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(updates),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({ error: 'Unknown error' }));
		throw new Error(err.error || `Failed to update tag: ${res.status}`);
	}
	return res.json();
}

// Tag prefix grouping for the table
function getTagPrefix(name: string): string {
	const colonIndex = name.indexOf(':');
	if (colonIndex === -1) return 'other';
	return name.slice(0, colonIndex);
}

function getGroupLabel(prefix: string): string {
	const labels: Record<string, string> = {
		category: 'Categories',
		domain: 'Domains',
		platform: 'Platforms',
		reddit: 'Reddit',
		instagram: 'Instagram',
		twitter: 'Twitter',
		onlyfans: 'OnlyFans',
		fansly: 'Fansly',
		tiktok: 'TikTok',
		pornhub: 'PornHub',
		redgifs: 'RedGifs',
		coomer: 'Coomer',
		hashtag: 'Hashtags',
		youtube: 'YouTube',
		other: 'Other',
	};
	return labels[prefix] || prefix;
}

// Sort order for groups
function getGroupOrder(prefix: string): number {
	const order: Record<string, number> = {
		category: 0,
		platform: 1,
		reddit: 2,
		instagram: 3,
		twitter: 4,
		onlyfans: 5,
		fansly: 6,
		tiktok: 7,
		youtube: 8,
		pornhub: 9,
		redgifs: 10,
		coomer: 11,
		hashtag: 12,
		domain: 13,
		other: 14,
	};
	return order[prefix] ?? 99;
}

interface TagEditDialogProps {
	tag: FullTagResponse | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSave: (id: number, updates: { name?: string; color?: string; icon?: string; autoDownload?: boolean }) => Promise<void>;
}

function TagEditDialog({ tag, open, onOpenChange, onSave }: TagEditDialogProps) {
	const [name, setName] = useState('');
	const [color, setColor] = useState('');
	const [icon, setIcon] = useState('');
	const [autoDownload, setAutoDownload] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (tag) {
			setName(tag.name);
			setColor(tag.color || '');
			setIcon(tag.icon || '');
			setAutoDownload(tag.autoDownload ?? false);
			setError(null);
		}
	}, [tag]);

	async function handleSave() {
		if (!tag) return;
		setSaving(true);
		setError(null);

		try {
			const updates: Record<string, unknown> = {};
			if (name !== tag.name) updates.name = name;
			if (color !== (tag.color || '')) updates.color = color || undefined;
			if (icon !== (tag.icon || '')) updates.icon = icon || undefined;
			if (autoDownload !== (tag.autoDownload ?? false)) updates.autoDownload = autoDownload;

			if (Object.keys(updates).length === 0) {
				onOpenChange(false);
				return;
			}

			await onSave(tag.id, updates as { name?: string; color?: string; icon?: string; autoDownload?: boolean });
			onOpenChange(false);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to save');
		} finally {
			setSaving(false);
		}
	}

	if (!tag) return null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[420px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Pencil className="size-4" />
						Edit Tag
					</DialogTitle>
					<DialogDescription>
						Configure tag properties. Changes apply globally.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-2">
					{/* Name */}
					<div className="space-y-1.5">
						<Label htmlFor="tag-name">Name</Label>
						<Input
							id="tag-name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="category:videos"
						/>
					</div>

					{/* Color + Icon row */}
					<div className="flex gap-3">
						<div className="space-y-1.5 flex-1">
							<Label htmlFor="tag-color">Color</Label>
							<div className="flex gap-2 items-center">
								<Input
									id="tag-color"
									value={color}
									onChange={(e) => setColor(e.target.value)}
									placeholder="#ef4444"
									className="flex-1"
								/>
								{color && /^#[0-9a-fA-F]{6}$/.test(color) && (
									<div
										className="size-8 rounded border shrink-0"
										style={{ backgroundColor: color }}
									/>
								)}
							</div>
						</div>
						<div className="space-y-1.5 w-20">
							<Label htmlFor="tag-icon">Icon</Label>
							<Input
								id="tag-icon"
								value={icon}
								onChange={(e) => setIcon(e.target.value)}
								placeholder="🔥"
								maxLength={4}
							/>
						</div>
					</div>

					{/* Auto-download toggle */}
					<div className="flex items-center justify-between rounded-lg border p-3">
						<div className="space-y-0.5">
							<Label className="flex items-center gap-1.5">
								<Download className="size-3.5" />
								Auto-download
							</Label>
							<p className="text-xs text-muted-foreground">
								Links with this tag will be automatically downloaded
							</p>
						</div>
						<Switch
							checked={autoDownload}
							onCheckedChange={setAutoDownload}
						/>
					</div>

					{/* Info badges */}
					<div className="flex gap-2 flex-wrap text-xs">
						{tag.isNsfw && <Badge variant="destructive">NSFW</Badge>}
						{tag.linkCount !== undefined && (
							<Badge variant="secondary">{tag.linkCount} links</Badge>
						)}
						{tag.isPersonal && <Badge variant="outline">Personal</Badge>}
					</div>

					{/* Error */}
					{error && (
						<p className="text-sm text-red-500">{error}</p>
					)}
				</div>

				{/* Actions */}
				<div className="flex justify-end gap-2">
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
						Cancel
					</Button>
					<Button onClick={handleSave} disabled={saving}>
						{saving ? <Loader2 className="size-4 animate-spin mr-1" /> : <Save className="size-4 mr-1" />}
						Save
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}

// Event system for opening tag admin from anywhere (ALT+click)
export const TAG_ADMIN_EVENT = 'open-tag-admin';

export interface TagAdminEvent {
	tagId?: number;
}

export function openTagAdmin(tagId?: number) {
	document.dispatchEvent(new CustomEvent(TAG_ADMIN_EVENT, { detail: { tagId } }));
}

export default function TagAdminPage() {
	const [tags, setTags] = useState<FullTagResponse[]>([]);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [editingTag, setEditingTag] = useState<FullTagResponse | null>(null);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [filterAutoDownload, setFilterAutoDownload] = useState(false);
	const searchRef = useRef<HTMLInputElement>(null);

	const loadTags = useCallback(async () => {
		try {
			setLoading(true);
			const data = await fetchAllTags();
			setTags(data);
		} catch (err) {
			console.error('Failed to load tags:', err);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadTags();
	}, [loadTags]);

	// Listen for external tag-admin open events (ALT+click)
	useEffect(() => {
		function handleOpenTagAdmin(e: Event) {
			const detail = (e as CustomEvent<TagAdminEvent>).detail;
			if (detail?.tagId) {
				const tag = tags.find((t) => t.id === detail.tagId);
				if (tag) {
					setEditingTag(tag);
					setEditDialogOpen(true);
				}
			}
		}
		document.addEventListener(TAG_ADMIN_EVENT, handleOpenTagAdmin);
		return () => document.removeEventListener(TAG_ADMIN_EVENT, handleOpenTagAdmin);
	}, [tags]);

	// Focus search on mount
	useEffect(() => {
		setTimeout(() => searchRef.current?.focus(), 100);
	}, []);

	const handleSave = useCallback(async (id: number, updates: { name?: string; color?: string; icon?: string; autoDownload?: boolean }) => {
		const updated = await updateTag(id, updates);
		setTags((prev) => prev.map((t) => (t.id === id ? { ...t, ...updated } : t)));
	}, []);

	// Filtered and grouped tags
	const filteredTags = useMemo(() => {
		let result = tags;

		if (search) {
			const q = search.toLowerCase();
			result = result.filter((t) => t.name.toLowerCase().includes(q));
		}

		if (filterAutoDownload) {
			result = result.filter((t) => t.autoDownload);
		}

		return result;
	}, [tags, search, filterAutoDownload]);

	// Group by prefix
	const grouped = useMemo(() => {
		const groups = new Map<string, FullTagResponse[]>();
		for (const tag of filteredTags) {
			const prefix = getTagPrefix(tag.name);
			if (!groups.has(prefix)) groups.set(prefix, []);
			groups.get(prefix)!.push(tag);
		}
		// Sort groups by order, then tags alphabetically within
		return Array.from(groups.entries())
			.sort((a, b) => getGroupOrder(a[0]) - getGroupOrder(b[0]))
			.map(([prefix, groupTags]) => ({
				prefix,
				label: getGroupLabel(prefix),
				tags: groupTags.sort((a, b) => a.name.localeCompare(b.name)),
			}));
	}, [filteredTags]);

	const autoDownloadCount = tags.filter((t) => t.autoDownload).length;

	if (loading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Loader2 className="size-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Header controls */}
			<div className="flex items-center gap-3">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
					<Input
						ref={searchRef}
						placeholder="Filter tags..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-8"
					/>
					{search && (
						<button
							type="button"
							onClick={() => setSearch('')}
							className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
						>
							<X className="size-4" />
						</button>
					)}
				</div>
				<div className="flex items-center gap-2 shrink-0">
					<Switch
						id="filter-auto-dl"
						checked={filterAutoDownload}
						onCheckedChange={setFilterAutoDownload}
					/>
					<Label htmlFor="filter-auto-dl" className="text-xs whitespace-nowrap flex items-center gap-1">
						<Download className="size-3" />
						Auto-DL only
						{autoDownloadCount > 0 && (
							<Badge variant="secondary" className="text-[10px] px-1 py-0">{autoDownloadCount}</Badge>
						)}
					</Label>
				</div>
			</div>

			{/* Stats bar */}
			<div className="flex gap-3 text-xs text-muted-foreground">
				<span>{tags.length} total tags</span>
				<span>{filteredTags.length} shown</span>
				{autoDownloadCount > 0 && (
					<span className="text-blue-500">{autoDownloadCount} auto-download</span>
				)}
			</div>

			{/* Tag table */}
			<div className="rounded-md border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-[40%]">Tag</TableHead>
							<TableHead className="w-20 text-center">Color</TableHead>
							<TableHead className="w-16 text-center">Icon</TableHead>
							<TableHead className="w-16 text-center">NSFW</TableHead>
							<TableHead className="w-20 text-center">Auto-DL</TableHead>
							<TableHead className="w-20 text-right">Links</TableHead>
							<TableHead className="w-16" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{grouped.map((group) => (
							<>
								{/* Group header */}
								<TableRow key={`group-${group.prefix}`} className="bg-muted/50 hover:bg-muted/50">
									<TableCell colSpan={7} className="py-1.5 font-semibold text-xs text-muted-foreground uppercase tracking-wide">
										{group.label} ({group.tags.length})
									</TableCell>
								</TableRow>
								{/* Tags in group */}
								{group.tags.map((tag) => (
									<TableRow
										key={tag.id}
										className="cursor-pointer hover:bg-muted/30"
										onClick={() => {
											setEditingTag(tag);
											setEditDialogOpen(true);
										}}
									>
										<TableCell className="font-mono text-xs">
											{tag.name}
										</TableCell>
										<TableCell className="text-center">
											{tag.color ? (
												<div className="flex items-center justify-center gap-1.5">
													<div
														className="size-4 rounded border"
														style={{ backgroundColor: tag.color }}
													/>
													<span className="text-[10px] text-muted-foreground font-mono">{tag.color}</span>
												</div>
											) : (
												<span className="text-muted-foreground/40 text-xs">-</span>
											)}
										</TableCell>
										<TableCell className="text-center">
											{tag.icon || <span className="text-muted-foreground/40 text-xs">-</span>}
										</TableCell>
										<TableCell className="text-center">
											{tag.isNsfw && <Badge variant="destructive" className="text-[10px] px-1 py-0">NSFW</Badge>}
										</TableCell>
										<TableCell className="text-center">
											{tag.autoDownload && (
												<Badge className="text-[10px] px-1 py-0 bg-blue-500/20 text-blue-400 border-blue-500/40">
													<Download className="size-2.5 mr-0.5" />
													ON
												</Badge>
											)}
										</TableCell>
										<TableCell className="text-right text-xs text-muted-foreground">
											{tag.linkCount ?? '-'}
										</TableCell>
										<TableCell>
											<Button
												variant="ghost"
												size="sm"
												className="size-7 p-0"
												onClick={(e) => {
													e.stopPropagation();
													setEditingTag(tag);
													setEditDialogOpen(true);
												}}
											>
												<Pencil className="size-3.5" />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</>
						))}
						{filteredTags.length === 0 && (
							<TableRow>
								<TableCell colSpan={7} className="text-center text-muted-foreground py-8">
									{search ? `No tags matching "${search}"` : 'No tags found'}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>

			{/* Edit dialog */}
			<TagEditDialog
				tag={editingTag}
				open={editDialogOpen}
				onOpenChange={setEditDialogOpen}
				onSave={handleSave}
			/>
		</div>
	);
}
